// src/pages/OnderwerpPage.js
// Publieke pagina per onderwerp (bijv. "Meer rust in de klas"):
// toont de gekoppelde gratis doorloopjes, met onderaan een registratie-CTA.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

import Header from '../components/Header';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import RegistrationModal from '../components/RegistrationModal';
import InfoModal from '../components/InfoModal';
import PublicDoorloopjeCard from '../components/PublicDoorloopjeCard';
import SEO from '../components/SEO';
import { usePubliekeDoorloopjes } from '../utils/publiekeDoorloopjes';
import { voorvertoningPad } from '../utils/publiekOverzicht';
import './PubliekeDoorloopjes.css';
import { ReactComponent as ChevronUpIcon } from '../assets/icon-chevron-up.svg';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;

function OnderwerpPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [onderwerp, setOnderwerp] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { doorloopjes: publiekeDoorloopjes } = usePubliekeDoorloopjes();
  // Alleen bij een echte 'bestaat niet' krijgt de pagina noindex. Bij een tijdelijke
  // laadfout niet: Google zou de pagina dan uit de index halen.
  const [notFound, setNotFound] = useState(false);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [privacyData, setPrivacyData] = useState(null);
  const [contactData, setContactData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const populate =
          'populate[uitleg_stappen][populate][icon]=true' +
          '&populate[uitleg_stappen][populate][doorloopjes][populate][steps][populate][image]=true' +
          '&populate[uitleg_stappen][populate][doorloopjes][populate][theme]=true';
        const res = await axios.get(`${STRAPI_URL}/api/landingpages?${populate}`);
        const landing = res.data?.data?.[0];
        const stap = landing?.uitleg_stappen?.find((s) => s.slug === slug);
        if (stap) setOnderwerp(stap);
        else {
          setError('Pagina niet gevonden');
          setNotFound(true);
        }
      } catch (err) {
        console.error('Fout bij het ophalen van het onderwerp:', err);
        setError('Deze pagina laden lukte niet. Probeer het opnieuw.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const [privacyRes, contactRes, settingsRes] = await Promise.all([
          axios.get(`${STRAPI_URL}/api/privacyverklaring?populate=*`),
          axios.get(`${STRAPI_URL}/api/contact-pagina?populate=*`),
          axios.get(`${STRAPI_URL}/api/global-setting?populate[0]=logo`),
        ]);
        setPrivacyData(privacyRes.data.data);
        setContactData(contactRes.data.data);
        setGlobalSettings(settingsRes.data.data);
      } catch (err) {
        console.error('Fout bij het ophalen van footer data:', err);
      }
    };
    fetchFooterData();
  }, []);

  const handleLoginClick = () => setIsLoginModalOpen(true);
  const handleRegisterClick = () => setIsRegisterModalOpen(true);

  const scaffolding = (children) => (
    <div className="page-container onderwerp-page">
      <Header onLoginClick={handleLoginClick} onRegisterClick={handleRegisterClick} />
      {isLoginModalOpen && (
        <LoginModal
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => {
            setIsLoginModalOpen(false);
            window.location.href = '/dashboard';
          }}
          onOpenRegister={() => {
            setIsLoginModalOpen(false);
            setIsRegisterModalOpen(true);
          }}
        />
      )}
      {isRegisterModalOpen && (
        <RegistrationModal
          onClose={() => setIsRegisterModalOpen(false)}
          onRegisterSuccess={() => {
            setIsRegisterModalOpen(false);
            window.location.href = '/dashboard';
          }}
        />
      )}
      <InfoModal
        show={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        title={privacyData?.title}
        content={privacyData?.body}
        loading={false}
        showSocial={false}
      />
      <InfoModal
        show={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        title={contactData?.title}
        content={contactData?.body}
        loading={false}
        showSocial={true}
      />
      {children}
      <Footer
        onLoginClick={handleLoginClick}
        onPrivacyClick={() => setIsPrivacyModalOpen(true)}
        onContactClick={() => setIsContactModalOpen(true)}
      />
    </div>
  );

  if (loading) return scaffolding(<main><p style={{ padding: '2rem' }}>Pagina wordt geladen...</p></main>);

  if (error || !onderwerp)
    return scaffolding(
      <main>
        <SEO
          title="Pagina niet gevonden | Doorloopjes"
          description="Dit onderwerp bestaat niet of is verplaatst."
          noindex={notFound}
        />
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>Pagina niet gevonden</h1>
          <p>{error || 'Dit onderwerp bestaat niet of is verplaatst.'}</p>
        </div>
      </main>
    );

  const doorloopjes = onderwerp.doorloopjes || [];

  return scaffolding(
    <>
      <SEO
        title={`${onderwerp.title} | Doorloopjes`}
        description={onderwerp.page_text || onderwerp.description || ''}
        url={`/onderwerp/${slug}`}
        type="article"
      />
      <main>
        <div className="onderwerp-content">
          <button className="back-button" onClick={() => navigate('/')}>
            <ChevronUpIcon className="back-icon" />
            <span>Terug</span>
          </button>

          <h1>{onderwerp.title}</h1>
          {(onderwerp.page_text || onderwerp.description) && (
            <p className="onderwerp-intro">{onderwerp.page_text || onderwerp.description}</p>
          )}

          {doorloopjes.length === 0 ? (
            <p>Bij dit onderwerp staan nog geen doorloopjes.</p>
          ) : (
            doorloopjes.map((dl) => (
              <PublicDoorloopjeCard
                key={dl.documentId || dl.id}
                doorloopje={dl}
                voorvertoning={publiekeDoorloopjes?.some((p) => p.type === 'doorloopje' && p.slug === dl.slug) ? voorvertoningPad(dl.slug) : null}
                logoUrl={globalSettings?.logo?.url}
              />
            ))
          )}

          <div className="example-cta-section">
            <div className="example-cta-content">
              <h3>Meer doorloopjes bekijken?</h3>
              <p>Maak gratis een account en lees direct verder.</p>
              <button onClick={handleRegisterClick} className="btn-primary">
                Gratis registreren
              </button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default OnderwerpPage;
